import React, { useCallback, useEffect, useState } from "react"
import clsx from "clsx"
import ReactInputMask from "react-input-mask"
import moment from "moment"

import {
  canParseDateStr,
  DATE_MASK,
  DIVIDER,
  FULL_DATE_FORMAT,
  parseDateStr,
  parsePeriodStr,
  validateDate,
  validatePeriod,
  normalizeDateRange,
} from "./utils"
import { Icon } from "../Icon/Icon"
import Tooltip from "../Tooltip/Tooltip"
import TooltipContainer from "../Tooltip/TooltipContainer"
import Calendar from "./Calendar"
import Chip from "../Chip/Chip"

import styles from "./DatePicker.module.sass"

export type DateRangePresetOption<T extends Date | [Date, Date]> = {
  name: string
  value: () => T
}

const datesToString = (value: Date | Date[]) =>
  !Array.isArray(value)
    ? moment(value).format(FULL_DATE_FORMAT)
    : value?.map((curr) => moment(curr).format(FULL_DATE_FORMAT)).join(DIVIDER)

export interface MultiDatePickerType {
  multiSelect?: true
  defValue?: Date[]
  presets?: DateRangePresetOption<[Date, Date]>[] | null
  onChange?: (date?: Date[]) => void
}

export interface SingleDatePickerType {
  multiSelect?: false
  defValue?: Date
  presets?: DateRangePresetOption<Date>[] | null
  onChange?: (date?: Date) => void
}

export type DatePickerProps = {
  multiSelect?: boolean
  minDate?: Date
  maxDate?: Date
  placeholder?: string
  className?: string
  onChange?: (date?: Date | Date[]) => void
  onBlur?: (e: any) => void
} & (MultiDatePickerType | SingleDatePickerType)

export const DatePicker: React.FC<DatePickerProps> = ({
  multiSelect = true,
  defValue,
  minDate,
  maxDate,
  placeholder,
  presets,
  className,
  onChange,
  onBlur,
}) => {
  const [isFocused, setFocused] = useState(false)
  const [isOpen, setOpen] = useState(false)
  const [date, setDate] = useState<typeof defValue>()
  const [value, setValue] = useState<string>("")

  const isDateValidator = (date: Date | Date[]) =>
    multiSelect ? validatePeriod(date as Date[]) : validateDate(date as Date)

  const parser: (date: string) => Date | Date[] = multiSelect
    ? parsePeriodStr
    : parseDateStr

  const mask = multiSelect
    ? `${DATE_MASK}${DIVIDER}${DATE_MASK}`
    : `${DATE_MASK}`

  const isTouched = !!value
  const isValid = canParseDateStr(value) && date ? isDateValidator(date) : false

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setValue(value)
    if (!value || !canParseDateStr(value)) {
      if (defValue !== undefined && onChange) onChange(undefined)
      return
    }
    const newDate = parser(value)
    if (isDateValidator(newDate)) {
      setDate(newDate)
      onChange?.(newDate)
    } else {
      setDate(undefined)
      onChange?.(undefined)
    }
  }

  const handleBlur = (e: any) => {
    setFocused(false)
    onBlur?.(e)
    if (!value || !canParseDateStr(value)) return
    if (isDateValidator(parser(value)) && date) {
      const normalized = normalizeDateRange(date as Date[])
      setValue(datesToString(normalized))
      onChange?.(normalized)
      setDate(normalized)
    }
  }

  const handlePick = useCallback(
    (value: Date | Date[]) => {
      const dateStr = datesToString(value)
      setValue(dateStr)
      setDate(value)
      onChange?.(value)
    },
    [onChange]
  )

  useEffect(() => {
    if (!defValue) {
      if (!isFocused) {
        setDate(defValue)
        setValue("")
      }
      return
    }
    if (datesToString(defValue) !== value) {
      setDate(defValue)
      setValue(datesToString(defValue))
    }
  }, [defValue])

  return (
    <div className={clsx(styles.root, className)}>
      <Icon className={styles.calendarIcon} name="calendar" />

      <ReactInputMask
        placeholder={placeholder}
        type="text"
        mask={mask}
        value={value}
        className={clsx(styles.input, {
          [styles.invalid]: !isFocused && !isValid && isTouched,
        })}
        onChange={handleInputChange}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
      />
      <Tooltip
        className={styles.tooltip}
        mode="onFocus"
        show={isOpen}
        onClose={() => setOpen(false)}
        justify="end"
        content={
          <TooltipContainer
            className={styles.tooltipContainer}
            placement="bottom"
            color="primary"
            tip="end"
          >
            <div className={styles.calendarContainer}>
              <Calendar
                minDate={minDate}
                maxDate={maxDate}
                selectRange={multiSelect}
                onChange={handlePick}
                value={date as Date} // FIXME
                className="react-calendar"
                formatMonthYear={(_, date) => moment(date).format("MMMM, yyyy")}
                formatMonth={(_, date) => moment(date).format("MMMM")}
                formatShortWeekday={(_, date) => moment(date).format("dd")}
              />
            </div>

            {!!presets?.length && (
              <div className={styles.presetContainer}>
                {presets.map((preset) => (
                  <Chip
                    color="secondary-solid"
                    variant="rounded"
                    onClick={() => {
                      handlePick(preset.value())
                      setOpen(false)
                    }}
                  >
                    {preset.name}
                  </Chip>
                ))}
              </div>
            )}
          </TooltipContainer>
        }
      >
        <span className={styles.arrow}>
          <Icon
            name="arrow-down"
            onClick={() => setOpen((value) => !value)}
            rotateAngle={isOpen ? -180 : 0}
          />
        </span>
      </Tooltip>
    </div>
  )
}

export default DatePicker
